import { Button } from '@mui/material';
import { useEffect, useRef } from 'react';

import { useInterval } from './hooks/useInterval';
import { useSlabQueryFetcher } from './hooks/useSlabQuery';
import { useToast } from './providers/ToastProvider/useToast';
import type { QueryRouteBarrelTypes } from './utils/ApiClient';
import type { QueryError } from './utils/Query';

const interval = 20_000;

export const AppUpdates = (): null => {
  const currentHashRef = useRef<string | null>(null);
  const isInitialLoad = useRef(true);
  const toast = useToast({
    position: 'bottomRight',
    config: { maxVisibleToasts: 1, autoDismissDuration: 0 },
  });
  const getAppVersion = useSlabQueryFetcher<
    'GET app version',
    QueryRouteBarrelTypes['GET app version'],
    QueryError
  >();

  const getHash = async (): Promise<string> =>
    (await getAppVersion('GET app version', {}, { staleTime: 0 })).hash;

  // Best-effort: a failed check (offline, tab waking from sleep) is retried on the next tick.
  useInterval(() => {
    getHash()
      .then((hash) => {
        if (hash !== currentHashRef.current) {
          currentHashRef.current = hash;
          if (!isInitialLoad.current) {
            toast.info({
              title: 'Slabstack has updated!',
              message: 'A new version of the app is available.',
              action: (
                <Button color='inherit' size='small' onClick={(): void => window.location.reload()}>
                  REFRESH
                </Button>
              ),
            });
          }
          isInitialLoad.current = false;
        }
      })
      .catch((): void => undefined);
  }, interval);

  useEffect(() => {
    getHash()
      .then((hash) => {
        currentHashRef.current = hash;
        isInitialLoad.current = false;
      })
      .catch((): void => undefined);
  }, []);

  return null;
};
